import type {
  CommandGroup,
  CommandItem,
} from "@replohq/lattice/utils/commands";

import * as React from "react";

import omit from "lodash-es/omit";
import { createShallowStore } from "replo-utils/react/zustand";

export const LEGACY_COMMAND_GROUPS: {
  id: CommandGroup["id"];
  heading: string;
}[] = [
  { id: "text", heading: "Text" },
  { id: "edit", heading: "Edit" },
  { id: "add", heading: "Add" },
  { id: "view", heading: "View" },
  { id: "create", heading: "Create" },
  { id: "navigate", heading: "Navigate to..." },
  { id: "debug", heading: "Debug" },
  { id: "debugOptions", heading: "Debug Options" },
];

type DynamicCommandItemVisibility = "always" | "triggeredByKeyboard";

export type CommandItemDisplayState = {
  description?: string;
  isActive?: boolean;
};

export type DynamicCommandItem = CommandItem & {
  // A LEGACY_COMMAND_GROUPS id, or a free-form heading for a group that isn't
  // one (e.g. a miniapp's "Hello World - Actions"). The grouping below renders
  // unknown group ids as their own heading rather than dropping the item.
  group: CommandGroup["id"];
  // Arbitrary tags for bulk operations: a caller can enable/disable or remove
  // every item carrying a given tag at once. The command system is agnostic to
  // what tags mean — e.g. the miniapp bridge tags its items with `miniapp:<id>`
  // so it can hide/purge them without the store knowing about miniapps.
  tags?: string[];
  visibility?: DynamicCommandItemVisibility;
} & CommandItemDisplayState;

type CommandMenuStore = {
  items: Record<string, DynamicCommandItem>;
  // Keys of items currently hidden. `setTagEnabled` resolves a tag to the keys
  // of the items carrying it at toggle time and flips them here, so reads only
  // need a direct membership check.
  disabledItemIds: Record<string, true>;
  addItem: (key: string, item: DynamicCommandItem) => void;
  removeItem: (key: string) => void;
  removeItemsByTag: (tag: string) => void;
  setTagEnabled: (args: { tag: string; enabled: boolean }) => void;
};

const useCommandMenuStore = createShallowStore<CommandMenuStore>((set) => ({
  items: {},
  disabledItemIds: {},
  addItem: (key, item) => {
    return set((state) => ({ items: { ...state.items, [key]: item } }));
  },
  removeItem: (key) => {
    set((state) => ({
      items: omit(state.items, key),
      disabledItemIds: omit(state.disabledItemIds, key),
    }));
  },
  removeItemsByTag: (tag) => {
    set((state) => {
      const items = { ...state.items };
      const disabledItemIds = { ...state.disabledItemIds };
      for (const [key, item] of Object.entries(items)) {
        if (item.tags?.includes(tag)) {
          delete items[key];
          delete disabledItemIds[key];
        }
      }
      return { items, disabledItemIds };
    });
  },
  // Resolve the tag to the keys of items currently carrying it and flip their
  // hidden flag. Items registered after this call aren't affected, so callers
  // toggle once their items are already in the store.
  setTagEnabled: ({ tag, enabled }) => {
    set((state) => {
      const disabledItemIds = { ...state.disabledItemIds };
      for (const [key, item] of Object.entries(state.items)) {
        if (!item.tags?.includes(tag)) {
          continue;
        }
        if (enabled) {
          delete disabledItemIds[key];
        } else {
          disabledItemIds[key] = true;
        }
      }
      return { disabledItemIds };
    });
  },
}));

export function useAllDynamicCommandMenuItems(): Record<
  string,
  DynamicCommandItem
> {
  return useCommandMenuStore((state) => state.items);
}

export function useDynamicCommandMenuItem(
  key: string,
  item?: DynamicCommandItem | null,
) {
  const addItem = useCommandMenuStore((state) => state.addItem);
  const removeItem = useCommandMenuStore((state) => state.removeItem);

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    if (!item) {
      removeItem(key);
      return;
    }

    addItem(key, item);

    return () => {
      removeItem(key);
    };
  }, [key, item, addItem, removeItem]);
}

/**
 * Hook for registering multiple command menu items at once.
 * Items are automatically added on mount and removed on unmount.
 */
export function useDynamicCommandMenuItems(
  items: Record<string, DynamicCommandItem>,
) {
  const addItem = useCommandMenuStore((state) => state.addItem);
  const removeItem = useCommandMenuStore((state) => state.removeItem);

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    // Add all items
    Object.entries(items).forEach(([key, item]) => {
      addItem(key, item);
    });

    // Cleanup: remove all items when unmounting or items change
    return () => {
      Object.keys(items).forEach((key) => {
        removeItem(key);
      });
    };
  }, [items, addItem, removeItem]);
}
